/**
 * StakeholderDocumentsTab
 * Documents view for the StakeholderFormDialog detail modal.
 *
 * Two modes:
 *  - mode="investor" (default): loads documents for an investor and groups them
 *    by contract (with a separate "General / Investor Documents" group for docs
 *    not tied to a contract). Upload can link a doc to a contract.
 *  - mode="issuer": loads documents across all of an issuer's programs and groups
 *    them by program (with a separate "General / Issuer Documents" group). Upload
 *    can link a doc to a program.
 *
 * Supports collapse/expand per group. Used only on the stakeholder detail modal.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { apiGet, apiUpload, apiDelete } from '../../utils/apiClient';
import { useNotification } from '../../contexts/NotificationContext';
import useAsyncData from '../../hooks/useAsyncData';
import useDocumentActions from '../../hooks/useDocumentActions';
import DocumentPreviewDialog from '../documents/DocumentPreviewDialog';
import { formatDate, formatFileSize } from '../../utils/formatters';
import { getStatusColor } from '../../utils/statusColors';
import FileDropZone from './FileDropZone';

const GENERAL_GROUP_KEY = '__general__';

const FALLBACK_DOCUMENT_TYPES = [
  { code: 'kyc', display_name: 'KYC Document' },
  { code: 'proof_of_payment', display_name: 'Proof of Payment' },
  { code: 'bank_statement', display_name: 'Bank Statement' },
  { code: 'contract', display_name: 'Contract Document' },
  { code: 'supporting_doc', display_name: 'Supporting Document' },
  { code: 'other', display_name: 'Other' },
];

const getDocStatusColor = (status) => {
  switch (status) {
    case 'approved': return 'success';
    case 'pending': return 'warning';
    case 'rejected': return 'error';
    case 'reupload_requested': return 'info';
    default: return 'default';
  }
};

const StakeholderDocumentsTab = ({ mode = 'investor', investorId, issuerId, contracts = [], programs = [] }) => {
  const isIssuerMode = mode === 'issuer';
  // The list of selectable parents in the upload "Link to…" dropdown and for
  // grouping: contracts (investor mode) or programs (issuer mode).
  const parents = isIssuerMode ? programs : contracts;

  const notify = useNotification();
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [documentTypes, setDocumentTypes] = useState(FALLBACK_DOCUMENT_TYPES);
  const docActions = useDocumentActions();

  // Upload state. parent_id holds the linked contract_id (investor) or
  // program_id (issuer); it's posted under the right field name on submit.
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({ document_type: '', notes: '', file: null, parent_id: '' });
  const [uploading, setUploading] = useState(false);

  // Delete state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load document types
  useEffect(() => {
    apiGet('/refdata/document-types/active')
      .then((res) => {
        if (res?.data?.length) setDocumentTypes(res.data);
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  // Load documents for this investor (by investor) or issuer (across its programs)
  const ownerId = isIssuerMode ? issuerId : investorId;
  const {
    data: documents,
    loading,
    reload: loadDocuments,
  } = useAsyncData(
    async () => {
      if (!ownerId) return [];
      const path = isIssuerMode
        ? `/admin/documents/by-issuer/${ownerId}`
        : `/admin/documents/by-investor/${ownerId}`;
      const res = await apiGet(path);
      return res?.data || [];
    },
    { defaultValue: [], fetchOnMount: !!ownerId, deps: [ownerId, isIssuerMode], extractData: false, onError: notify.error }
  );

  // Build a lookup of parent id -> parent row (contract or program)
  const parentsById = useMemo(() => {
    const map = new Map();
    for (const p of parents) map.set(p.id, p);
    return map;
  }, [parents]);

  // The grouping key for a document: program_id (issuer) or contract_id (investor)
  const groupKeyOf = useCallback(
    (doc) => {
      const id = isIssuerMode ? doc.program_id : doc.contract_id;
      return id ? String(id) : GENERAL_GROUP_KEY;
    },
    [isIssuerMode]
  );

  // Status options actually present on this investor's contracts (investor mode
  // only — programs use active/inactive, derived inline below).
  const availableStatuses = useMemo(() => {
    if (isIssuerMode) return [];
    const set = new Set();
    for (const c of contracts) {
      if (c.status) set.add(c.status);
    }
    return Array.from(set);
  }, [contracts, isIssuerMode]);

  // Group documents by parent (program_id / contract_id), or GENERAL when none
  const groups = useMemo(() => {
    const byKey = new Map();
    for (const doc of documents) {
      const key = groupKeyOf(doc);
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(doc);
    }

    const result = [];
    if (byKey.has(GENERAL_GROUP_KEY)) {
      result.push({
        key: GENERAL_GROUP_KEY,
        title: isIssuerMode ? 'General / Issuer Documents' : 'General / Investor Documents',
        reference: null,
        status: null,
        docs: byKey.get(GENERAL_GROUP_KEY),
        isGeneral: true,
      });
    }

    const parentKeys = Array.from(byKey.keys()).filter((k) => k !== GENERAL_GROUP_KEY);
    for (const k of parentKeys) {
      const parent = parentsById.get(parseInt(k, 10));
      const docs = byKey.get(k);
      let title;
      let status;
      if (isIssuerMode) {
        title = parent?.name || docs[0]?.program_name || `Program #${k}`;
        status = parent ? (parent.active ? 'Active' : 'Inactive') : null;
      } else {
        title = parent?.user_reference || parent?.reference || docs[0]?.contract_reference || `Contract #${k}`;
        status = parent?.status || null;
      }
      result.push({
        key: k,
        title,
        reference: title,
        status,
        docs,
        isGeneral: false,
      });
    }
    return result;
  }, [documents, parentsById, groupKeyOf, isIssuerMode]);

  const filteredGroups = useMemo(() => {
    if (statusFilter === 'all') return groups;
    return groups.filter((g) => g.isGeneral || g.status === statusFilter);
  }, [groups, statusFilter]);

  const toggleGroup = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getDocumentTypeLabel = (code) => {
    const found = documentTypes.find((t) => t.code === code);
    return found?.display_name || code;
  };

  const handleUpload = async () => {
    if (!uploadData.document_type || !uploadData.file) {
      notify.error('Please select a document type and file');
      return;
    }
    if (isIssuerMode && !uploadData.parent_id) {
      notify.error('Please select a program to link this document to');
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('document_type', uploadData.document_type);
      if (isIssuerMode) {
        // Issuer-side docs: link to a program (or general/issuer-level if blank).
        if (uploadData.parent_id) formData.append('program_id', uploadData.parent_id);
        // No investor context — the API derives the issuer from the program.
      } else {
        formData.append('investor_id', investorId);
        if (uploadData.parent_id) formData.append('contract_id', uploadData.parent_id);
      }
      if (uploadData.notes) formData.append('notes', uploadData.notes);

      await apiUpload('/admin/documents/upload', formData);
      notify.success('Document uploaded successfully');
      setUploadDialog(false);
      setUploadData({ document_type: '', notes: '', file: null, parent_id: '' });
      loadDocuments();
    } catch (err) {
      notify.error('Failed to upload document: ' + (err.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await apiDelete(`/admin/documents/${deleteTarget.id}`);
      notify.success('Document deleted');
      setDeleteDialog(false);
      setDeleteTarget(null);
      loadDocuments();
    } catch (err) {
      notify.error('Failed to delete document: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleting(false);
    }
  };

  const renderDocRow = useCallback((doc) => (
    <TableRow key={doc.id} hover>
      <TableCell>
        <Typography variant="body2" fontWeight="medium">{doc.file_name}</Typography>
        {doc.notes && (
          <Typography variant="caption" color="text.secondary">{doc.notes}</Typography>
        )}
      </TableCell>
      <TableCell>{getDocumentTypeLabel(doc.document_type)}</TableCell>
      <TableCell>{formatFileSize(doc.file_size)}</TableCell>
      <TableCell>
        <Typography variant="body2">{formatDate(doc.created_at)}</Typography>
        {doc.uploaded_by_email && (
          <Typography variant="caption" color="text.secondary">{doc.uploaded_by_email}</Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip label={doc.status} size="small" color={getDocStatusColor(doc.status)} />
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Preview">
          <IconButton size="small" onClick={() => docActions.openPreview(doc.id)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton size="small" onClick={() => docActions.downloadDocument(doc.id)}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => { setDeleteTarget(doc); setDeleteDialog(true); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [documentTypes, docActions]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        {isIssuerMode ? (
          <Box />
        ) : (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Contract Status</InputLabel>
            <Select
              value={statusFilter}
              label="Contract Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {availableStatuses.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={<CloudUploadIcon />}
          onClick={() => setUploadDialog(true)}
        >
          Upload
        </Button>
      </Box>

      {filteredGroups.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">
            {documents.length === 0 ? 'No documents uploaded yet' : 'No documents match the selected filter'}
          </Typography>
        </Box>
      ) : (
        filteredGroups.map((group) => {
          const isOpen = !!expanded[group.key];
          return (
            <Paper key={group.key} variant="outlined" sx={{ mb: 1 }}>
              <Box
                onClick={() => toggleGroup(group.key)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <IconButton size="small" sx={{ mr: 1 }}>
                  {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Typography variant="body2" fontWeight="medium" sx={{ flexGrow: 1 }}>
                  {group.title}
                </Typography>
                {group.status && (
                  <Chip
                    label={group.status}
                    size="small"
                    color={getStatusColor(group.status)}
                    sx={{ mr: 1 }}
                  />
                )}
                <Chip label={group.docs.length} size="small" />
              </Box>
              <Collapse in={isOpen}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>File Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Uploaded</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.docs.map(renderDocRow)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Paper>
          );
        })
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={uploadData.document_type}
                label="Document Type"
                onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
              >
                {documentTypes.map((type) => (
                  <MenuItem key={type.code} value={type.code}>{type.display_name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {isIssuerMode ? (
              <FormControl fullWidth required>
                <InputLabel>Link to Program</InputLabel>
                <Select
                  value={uploadData.parent_id}
                  label="Link to Program"
                  onChange={(e) => setUploadData({ ...uploadData, parent_id: e.target.value })}
                >
                  {programs.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}{p.active === false ? ' — Inactive' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Link to Contract (Optional)</InputLabel>
                <Select
                  value={uploadData.parent_id}
                  label="Link to Contract (Optional)"
                  onChange={(e) => setUploadData({ ...uploadData, parent_id: e.target.value })}
                >
                  <MenuItem value="">General / Investor-level</MenuItem>
                  {contracts.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {(c.user_reference || c.reference)}{c.status ? ` — ${c.status}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Notes (Optional)"
              multiline
              rows={2}
              value={uploadData.notes}
              onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
            />

            <FileDropZone
              file={uploadData.file}
              onFile={(f) => setUploadData({ ...uploadData, file: f })}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              helperText="PDF, JPG, PNG, DOC, DOCX, XLS, XLSX (max 10MB)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || !uploadData.file || !uploadData.document_type || (isIssuerMode && !uploadData.parent_id)}
          >
            {uploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "{deleteTarget?.file_name}"?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentPreviewDialog {...docActions} />
    </Box>
  );
};

export default StakeholderDocumentsTab;
